import { useLocalStorage } from '@vueuse/core';
import { acceptHMRUpdate, defineStore } from 'pinia';
import { computed, ref } from 'vue';
import posthog from "posthog-js";
import router from '@/router';

export interface PsEstimationType {
  token: string | undefined;
  active: boolean;
  exp: number;
  iat: number;
}

export interface UserData {
  email: string;
  id: string | undefined;
  role: string;
  permissions: Array<string>;
  employee_id: string | undefined;
  name: string | undefined;
}

export interface Token {
  token: string;
}

export const useUserSession = defineStore('userSession', () => {
  // token will be synced with local storage
  // @see https://vueuse.org/core/usestorage/
  const token = useLocalStorage('token', '');
  const selectedtTaskId = useLocalStorage('selectedtTaskId', '');
  const userDeviceToken = useLocalStorage('userDeviceToken', '');

  const user = ref<UserData>(
    useLocalStorage('vueuseuser', {
      email: '',
      id: '',
      role: '',
      permissions: [],
      employee_id: '',
      name: '',
    }) as any,
  );

  const loading = ref(true);

  const isLoggedIn = computed(() => token.value !== '');

  function setUser(newUser: UserData) {
    user.value = newUser;
  }

  function setToken(newToken: string) {
    token.value = newToken;
  }

  function setLoading(newLoading: boolean) {
    loading.value = newLoading;
  }

  function setSelectedTaskId(task_id: string) {
    selectedtTaskId.value = task_id
  }

  function resetSelectedTaskId() {
    selectedtTaskId.value = ''
  }

  function setUserDeviceToken(token: string) {
    userDeviceToken.value = token;
  }

  function resetUserDeviceToken() {
    userDeviceToken.value = '';
  }

  async function logoutUser() {
    if ("setAppBadge" in window.navigator) {
      const nav: any = window.navigator;
      nav.clearAppBadge();
    }
    token.value = '';
    user.value = {
      email: '',
      id: undefined,
      role: '',
      permissions: [],
      employee_id: '',
      name: '',
    };
    resetUserDeviceToken();
    posthog.reset();
    router.push('/login');
  }

  async function login(data: UserData & Token) {
    const userDetail = {
      email: data.email,
      id: data.id,
      role: data.role,
      permissions: data.permissions,
      employee_id: data.employee_id,
      name: data.name,
    };

    // store user details and jwt in local storage to keep user logged in between page refreshes
    setToken(data.token);
    token.value = data.token;
    setUser(userDetail);

    // redirect to previous url or default to home page
    // router.push(this.returnUrl || '/');
  }

  return {
    user,
    token,
    isLoggedIn,
    loading,
    logoutUser,
    setUser,
    setToken,
    setLoading,
    login,
    selectedtTaskId,
    setSelectedTaskId,
    resetSelectedTaskId,
    userDeviceToken,
    setUserDeviceToken,
    resetUserDeviceToken
  } as const;
});

/**
 * Pinia supports Hot Module replacement so you can edit your stores and
 * interact with them directly in your app without reloading the page.
 *
 * @see https://pinia.esm.dev/cookbook/hot-module-replacement.html
 * @see https://vitejs.dev/guide/api-hmr.html
 */
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserSession, import.meta.hot));
}
